import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../components/Loading";
import { MainButton } from "../../components/MainButton";
import { ConfigType } from "../../contexts/AppConfigContext";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useOrder } from "../../hooks/useOrder";
import { usePluginspace } from "../../hooks/usePluginspace";
import "./index.css";
import Swal from "sweetalert2";
import api from "../../services/api";
import InputMask from "react-input-mask";
import { Checkbox } from "../../components/Checkbox";
import { datetimeToDateBR, telephoneNoMask } from "../../functions/mask";

type ImageType = {
  id: string;
  url: string;
};

type HourType = {
  id: string;
  hour: string;
};

type DayType = {
  able: boolean;
  day:
    | "sunday"
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday";
  hours: HourType[];
};

type EnvironmentType = {
  id: string;
  name: string;
  seatsAvailable: number;
  tables: number;
};

type BookingConfigType = {
  id: string;
  able: boolean;
  dayConfirm: boolean;
  expiryTime: number;
  maxPeople: number;
  paymentConfirm: boolean;
  valuePaymentConfirm: number;
  days: DayType[];
  images: ImageType[];
  environment: EnvironmentType[];
  observation: string;
};

type BookingType = {
  id: string;
  name: string;
  phone: string;
  people: number;
  email: string;
  date: string;
  hour: HourType;
  bookingEnvironmentId: string;
};

export function BookingSuccess() {
  const { pluginspace_id, phone } = useParams();
  const { registerAccess, getToken } = usePluginspace();
  const navigate = useNavigate();

  const { getAppConfig } = useAppConfig();
  const [config, setConfig] = useState<ConfigType | void>();
  const [loading, setLoading] = useState(true);
  const [bookingConfig, setBookingConfig] = useState<BookingConfigType | null>(
    null
  );
  const [booking, setBooking] = useState<BookingType | null>(null);

  useEffect(() => {
    registerAccess("booking/success");
    init();
  }, []);

  async function init() {
    setLoading(true);
    await getConfig();
    await getBookingConfig();
    setLoading(false);
  }

  async function getBooking(bookingConfigId: number) {
    await api
      .get(`/api/queueReservation/booking/${bookingConfigId}/${phone}`, {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then((response) => {
        const bookingResponse = response.data;

        if (
          bookingResponse &&
          bookingResponse.data &&
          bookingResponse.data[bookingResponse.data.length - 1]
        ) {
          setBooking(bookingResponse.data[bookingResponse.data.length - 1]);
        } else {
          Swal.fire({
            icon: "error",
            title: "Erro!",
            text: "Não foi possível encontrar alguém na reserva com esse número de telefone.",
          }).then(() => navigate(`/${pluginspace_id}/booking`));
        }
      })
      .catch((err) => {
        let title = "Erro!";
        let text =
          "Não foi possível encontrar alguém na reserva com esse número de telefone.";
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.error
        ) {
          title = err.response.data.error;
        }
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          text = err.response.data.message;
        }
        Swal.fire({
          icon: "error",
          title,
          text,
        }).then(() => navigate(`/${pluginspace_id}/booking`));
      });
  }

  async function getBookingConfig() {
    await api
      .get("/api/queueReservation/bookingConfig", {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then(async (response) => {
        const bookingResponse = response.data;
        if (
          bookingResponse &&
          bookingResponse.data &&
          bookingResponse.data[0]
        ) {
          const bookingConfigItem = bookingResponse.data[0];
          if (bookingConfigItem && bookingConfigItem.able) {
            setBookingConfig(bookingConfigItem);

            await getBooking(bookingConfigItem.id);
          } else {
            Swal.fire({
              icon: "error",
              title: "Reserva não está habilitada!",
              text: "Loja não está com o esquema de reservas habilitada.",
            }).then(() => navigate(`/${pluginspace_id}`));
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Reserva não configurada!",
            text: "Loja não está com o esquema de reservas configurada.",
          }).then(() => navigate(`/${pluginspace_id}`));
        }
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({
          icon: "error",
          title: "Reserva não configurada!",
          text: "Loja não está com o esquema de reservas configurada.",
        }).then(() => navigate(`/${pluginspace_id}`));
      });
  }

  async function getConfig() {
    setConfig(await getAppConfig());
  }

  return (
    <div className="booking-find">
      {loading ? (
        <div style={{ width: "100%", height: "calc(100vh)" }}>
          <Loading type="spinningBubbles" color="#FFF" width={64} height={64} />
        </div>
      ) : (
        <div className="content-booking-find">
          {bookingConfig && booking && (
            <div className="body-booking-find">
              {bookingConfig.images && bookingConfig.images[1] && (
                <div className="booking-image">
                  <img
                    src={bookingConfig.images[1].url}
                    alt="Imagem Reserva 2"
                  />
                </div>
              )}
              <div className="booking-finded">
                <h2>Sua reserva está confirmada!</h2>
                {booking && (
                  <div className="booking-box">
                    <i className="fa fa-check-circle" aria-hidden="true"></i>
                    <div className="booking-info">
                      <div className="row">
                        <b>Data</b>
                        <p style={{ color: "#6E7586" }}>
                          {datetimeToDateBR(booking.date)}
                        </p>
                        <p style={{ color: "#6E7586" }}>{booking.hour.hour}</p>
                      </div>
                      <b>
                        {booking.people}{" "}
                        {booking.people > 1 ? "pessoas" : "pessoa"}
                      </b>
                      <b>{booking.name}</b>
                      {bookingConfig.environment?.length > 0 ? (
                        bookingConfig.environment
                          .filter(
                            ({ id }: any) =>
                              booking?.bookingEnvironmentId === id
                          )
                          .map(({ id, name }: any) => (
                            <div className="row" key={id}>
                              <p style={{ color: "#6E7586" }}>
                                {`Ambiente reservado: ${name}`}
                              </p>
                            </div>
                          ))
                      ) : (
                        <></>
                      )}

                      <div className="row">
                        <p style={{ color: "#6E7586" }}>{booking.phone}</p>
                      </div>
                      <div className="row">
                        <p style={{ color: "#6E7586" }}>{booking.email}</p>
                      </div>
                    </div>
                  </div>
                )}
                {bookingConfig.observation && (
                  <div
                    style={{ marginTop: "1rem" }}
                    className="observation-queue"
                    dangerouslySetInnerHTML={{
                      __html: bookingConfig.observation,
                    }}
                  ></div>
                )}
                <div className="buttons-booking-find">
                  <MainButton
                    color={
                      config?.webappcolor.webappbutton
                        ? config.webappcolor.webappbutton
                        : "#EAEAEA"
                    }
                    border={
                      config?.webappcolor.webappbutton
                        ? `1px solid ${config.webappcolor.webappbutton}`
                        : "1px solid #EAEAEA"
                    }
                    backgroundColor="transparent"
                    text="Alterar reserva"
                    onClick={() => {
                      navigate(
                        `/${pluginspace_id}/booking/update/${booking.id}`
                      );
                    }}
                  />
                  <MainButton
                    color={
                      config?.webappcolor.fontbutton
                        ? config.webappcolor.fontbutton
                        : "#636363"
                    }
                    backgroundColor={
                      config?.webappcolor.webappbutton
                        ? config.webappcolor.webappbutton
                        : "#EAEAEA"
                    }
                    text="Ver cardápio"
                    onClick={() => navigate(`/${pluginspace_id}`)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
